import { camelCase, snakeCase } from "lodash";
import { PropsWithChildren, ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useMobile } from "@krea/customer-features/hooks";
import LayoutOneColumn from "@krea/customer-features/shared-components/app/one-column";
import BankingData from "@krea/customer-features/shared-components/banking-data";
import Button from "@krea/customer-features/shared-components/button";
import AccountingDataForm from "@krea/customer-features/shared-components/field-request/forms/AccountingDataForm";
import { useGetCachedCurrentLoanApplication } from "@krea/customer-features/store/current-loan-application/hooks";
import { usePostFieldRequest } from "@krea/customer-features/store/field-requests/hooks/mutations";
import { useGetFieldRequests } from "@krea/customer-features/store/field-requests/hooks/queries";
import {
  FieldRequestFieldEnum,
  IFieldRequest,
} from "@krea/customer-features/types/fieldRequest";
import { RESOURCE_TYPE } from "@krea/customer-features/utils";
import { Label } from "@krea/shared/design-system/Label";
import {
  RadioGroup,
  RadioGroupItem,
} from "@krea/shared/design-system/RadioGroup";
import { Alert } from "@krea/shared/design-system/alerts/Alert";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@krea/shared/design-system/collapsible";
import { twMerge } from "@krea/shared/design-system/tailwind-utils";
import {
  IAccountingDataFRSubmittedData,
  IBankingDataFRSubmittedData,
} from "@krea/shared/types/fieldRequest";

interface IRevenueAdvicePageProps {
  onSendApplicationClick: () => void;
}

type RevenueAdviceOption =
  | FieldRequestFieldEnum.banking_data
  | FieldRequestFieldEnum.accounting_data
  | "noData";

interface IRevenueAdviceItemProps {
  selectedOption: RevenueAdviceOption;
  label: ReactNode;
  value: RevenueAdviceOption;
}

const RevenueAdviceItem = ({
  selectedOption,
  children,
  label,
  value,
}: PropsWithChildren<IRevenueAdviceItemProps>) => {
  const isSelected = selectedOption === value;

  return (
    <Collapsible
      open={isSelected}
      className={twMerge(
        "tw-rounded tw-border tw-px-2 tw-py-3 tw-text-primary tw-text-sm",
        {
          ["tw-border-primary tw-bg-primary-150"]: isSelected,
          ["tw-border-gray-200"]: !isSelected,
        },
      )}
    >
      <CollapsibleTrigger asChild>
        <Label
          htmlFor={value}
          className="tw-flex tw-items-center tw-space-x-2 "
        >
          <RadioGroupItem
            value={value}
            id={value}
            className={twMerge({ "tw-border-gray-200": !isSelected })}
          />
          <div>{label}</div>
        </Label>
      </CollapsibleTrigger>
      <CollapsibleContent>{children}</CollapsibleContent>
    </Collapsible>
  );
};

export const RevenueAdvice = ({
  onSendApplicationClick,
}: IRevenueAdvicePageProps) => {
  const isMobile = useMobile();
  const { t } = useTranslation();
  const { data } = useGetCachedCurrentLoanApplication();
  const { applyingCompany } = data ?? {};
  const { data: fieldRequests, refetch } = useGetFieldRequests();

  const [selectedOption, setSelectedOption] = useState<RevenueAdviceOption>(
    FieldRequestFieldEnum.banking_data,
  );

  const bankingDataFieldRequest = useMemo(
    () =>
      fieldRequests?.find(
        ({ field }) =>
          field ===
          (camelCase(
            FieldRequestFieldEnum.banking_data,
          ) as FieldRequestFieldEnum),
      ) as IFieldRequest<IBankingDataFRSubmittedData> | undefined,
    [fieldRequests],
  );
  const accountingDataFieldRequest = useMemo(
    () =>
      fieldRequests?.find(
        ({ field }) =>
          field ===
          (camelCase(
            FieldRequestFieldEnum.accounting_data,
          ) as FieldRequestFieldEnum),
      ) as IFieldRequest<IAccountingDataFRSubmittedData> | undefined,
    [fieldRequests],
  );

  const { mutate: postFieldRequest } = usePostFieldRequest(
    bankingDataFieldRequest?.id ?? "",
  );

  const onBankingDataSubmit = (data: any) => {
    postFieldRequest(
      { structured_data: data },
      {
        onSuccess: () => {
          refetch();
        },
      },
    );
  };

  const isNextButtonEnabled = useMemo(() => {
    switch (selectedOption) {
      case FieldRequestFieldEnum.banking_data:
        return !!bankingDataFieldRequest?.isSubmitted;
      case FieldRequestFieldEnum.accounting_data:
        return !!accountingDataFieldRequest?.isSubmitted;
      case "noData":
        return true;
      default:
        return false;
    }
  }, [selectedOption, bankingDataFieldRequest, accountingDataFieldRequest]);

  const SendInButton = () => (
    <Button
      block
      size="lg"
      tabIndex={0}
      className={"tw-mt-6 tw-w-full tw-text-center"}
      onClick={onSendApplicationClick}
      disabled={!isNextButtonEnabled}
    >
      {t("advice.digital.submitText")}
    </Button>
  );

  return (
    <LayoutOneColumn
      aside={
        <div
          className={twMerge({
            ["tw-w-full tw-px-6 tw-bg-white"]: isMobile,
          })}
          style={
            isMobile
              ? {
                  zIndex: "100000",
                  position: "fixed",
                  left: "0",
                  bottom: "0",
                  height: "100px",
                  boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.10)",
                }
              : { display: "none" }
          }
        >
          <SendInButton />
        </div>
      }
    >
      <h2 className="tw-my-5 tw-text-4xl tw-font-semibold">
        {t("advice.revenue.title")}
      </h2>

      <Alert variant="infoAlert" className="tw-my-5">
        {t("advice.digital.alertText")}
      </Alert>

      <RadioGroup
        value={selectedOption}
        onValueChange={(v) => setSelectedOption(v as RevenueAdviceOption)}
        className="tw-gap-3 tw-rounded tw-border tw-border-primary tw-px-4 tw-py-5"
      >
        <RevenueAdviceItem
          label={
            <>
              <span className="tw-text-sm tw-font-semibold">
                {t("advice.revenue.uploadBankingData")}
              </span>{" "}
              {t("advice.revenue.uploadBankingData2")}
            </>
          }
          value={FieldRequestFieldEnum.banking_data}
          selectedOption={selectedOption}
        >
          <div className="tw-mt-4">
            {applyingCompany?.id && (
              <BankingData
                isOnboarded={false}
                resourceId={applyingCompany.id}
                resourceType={RESOURCE_TYPE.APPLYING_COMPANY}
                lightTheme
                selectedAccounts={
                  bankingDataFieldRequest?.submittedData.structured_data
                }
                postSelectedAccounts={onBankingDataSubmit}
              />
            )}
          </div>
        </RevenueAdviceItem>

        <RevenueAdviceItem
          label={
            <span className="tw-font-semibold">
              {t("advice.revenue.uploadAccountingData")}
            </span>
          }
          value={FieldRequestFieldEnum.accounting_data}
          selectedOption={selectedOption}
        >
          <div className="tw-mt-4">
            <AccountingDataForm
              fieldRequestID={accountingDataFieldRequest?.id}
              isDelegated={false}
              isPreviewMode={false}
              withInputLabel={false}
              manuallyOnly
              onSuccessfulSubmit={() => {
                refetch();
              }}
            />
          </div>
        </RevenueAdviceItem>
        <RevenueAdviceItem
          label={
            <span className="tw-font-semibold">
              {t("advice.revenue.noData")}
            </span>
          }
          value={"noData"}
          selectedOption={selectedOption}
        />
      </RadioGroup>
      {!isMobile && <SendInButton />}
    </LayoutOneColumn>
  );
};
